import React, { useEffect, useRef, useState } from "react";
import { CardContent } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAbandonedCallReportQuery,
  useGetAllQueueQuery,
  useLazyGetAbandonedCallReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const formatGteDateTime = (date: string) =>
  date.includes(" ") ? date : `${date} 00:00:00`;

const formatLteDateTime = (date: string) =>
  date.includes(" ") ? date : `${date} 23:59:59`;

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const AbandonedCallsReport = () => {
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const { data: queueData, isLoading: queueLoading } = useGetAllQueueQuery({});
  const [params, setParams] = useState<any>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [reportFetching, setReportFetching] = useState(false);
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);
  const [allQueue, setAllQueue] = useState<any>([]);

  const [selectQueue, setSelectQueue] = useState<any>("");
  const [isNonWorkingHours, setIsNonWorkingHours] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const downloaderRef = useRef(null);

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL ||
    "https://reporting.bluewhirl.org";

  const {
    data: abandonedCallsReport,
    isFetching: liveReportFetching,
    status: liveReportStatus,
    error: abandonedCallsReportError,
  } = useGetAbandonedCallReportQuery(params, {
    skip: !params || reportMode !== "live",
  });
  const [triggerAbandonedCallDownload, { isFetching: downloadLoading }] =
    useLazyGetAbandonedCallReportDownloadQuery();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const columns: GridColDef[] = [
    {
      field: "sr.no",
      headerName: "Sr.no",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "date_connection",
      headerName: "Date and time of connection",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "caller_no",
      headerName: "Caller no",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "queue_name",
      headerName: "Queue name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ringing_duration",
      headerName: "Ringing duration",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_time",
      headerName: "Abandoned time",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
  ];

  const mapAbandonedItemToRow = (item: any, index: number, rowOffset: number) => {
    const sr = rowOffset + index + 1;
    return {
      id: sr,
      "sr.no": sr,
      date_connection: item.time || item.date_connection || "no data found",
      caller_no: item.customer_no ?? item.caller_no ?? "-",
      queue_name: item.queuename ?? item.queue_name ?? "-",
      ringing_duration: item.ringing_duration ?? "-",
      abandoned_time: item.abandoned_time ?? "-",
    };
  };

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  const handleQueueChange = (event: any) => {
    setSelectQueue(event.target.value);
  };

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  useEffect(() => {
    if (abandonedCallsReportError && reportMode === "live") {
      setDataRows([]);
      setLocalLoading(false);
    }
  }, [abandonedCallsReportError, reportMode]);

  useEffect(() => {
    const transformedData = queueData?.map((queue) => ({
      id: queue.id,
      name: queue.name,
    }));
    if (queueData && Array.isArray(queueData)) {
      setAllQueue(transformedData);
    }
  }, [queueData]);

  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
    };

    if (!isLive) {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
    }

    if (selectQueue !== "All") {
      const selectedQueue = allQueue.find(
        (queue: any) => queue.id === selectQueue
      );
      queryParams.queuename = selectedQueue?.name || selectQueue;
    }
    if (isNonWorkingHours) {
      queryParams.wh = true;
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setParams(queryParams);
  };

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setTotalRowsFromApi(null);
      }
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      toast.error("Session expired or not logged in. Please sign in again.");
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);
// ?gte=${gte}%2000:00:00&lte=${lte}%2023:59:59`
    const queryParams: Record<string, string | number | boolean> = {
      gte: params.gte,
      lte: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
    };
    if (params.queuename != null && params.queuename !== "") {
      queryParams.queuename = params.queuename;
    }
    if (params.wh === true) {
      queryParams.wh = true;
    }

    axios
      .get(`${reportingBaseUrl}/reports/telephony-abandoned-report`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
        const rowOffset = (apiPage - 1) * pageSizeNum;
        if (results.length > 0) {
          setDataRows(
            results.map((item: any, index: number) =>
              mapAbandonedItemToRow(item, index, rowOffset)
            )
          );
        } else {
          setDataRows([]);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(
          err?.response?.data?.message ||
            "Failed to fetch Abandoned Calls report"
        );
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (params) setLocalLoading(true);
  }, [params, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (abandonedCallsReport === undefined) {
      setLocalLoading(false);
      return;
    }

    if (Array.isArray(abandonedCallsReport)) {
      const transformedRows = abandonedCallsReport.map((item: any, index: number) =>
        mapAbandonedItemToRow(item, index, 0)
      );
      setDataRows(transformedRows);
    } else {
      setDataRows([]);
    }

    setLocalLoading(false);
  }, [abandonedCallsReport, reportMode]);

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setIsNonWorkingHours(false);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params || reportMode === "live") return;
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number | boolean> = {
      gte: params.gte,
      lte: params.lte,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
    };
    if (params.queuename != null && params.queuename !== "") {
      downloadPayload.queuename = params.queuename;
    }
    if (params.wh === true) {
      downloadPayload.wh = true;
    }

    try {
      await triggerAbandonedCallDownload(downloadPayload).unwrap();
      toast.success(
        "Report generation in progress. Download from exported report section once ready"
      );
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    queueLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            Abandoned calls{" "}
          </span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" },
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" },
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Queue-select-label">Queue</InputLabel>
              <Select
                labelId="Queue-select-label"
                id="Queue-select"
                label="Queue"
                value={selectQueue}
                onChange={handleQueueChange}
                MenuProps={MenuProps}
              >
                <MenuItem key="all" value="All">
                  All
                </MenuItem>
                {allQueue?.map((queue: any) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  ref={downloaderRef}
                  filename="abandoned-calls-report"
                  datas={dataRows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default AbandonedCallsReport;
